import React, { useState } from 'react';
import { SearchFilters } from '@/types/library';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

interface LibraryFiltersProps {
  filters: SearchFilters;
  onFilterChange: (filters: SearchFilters) => void;
  onReset: () => void;
}

const LibraryFilters: React.FC<LibraryFiltersProps> = ({
  filters,
  onFilterChange,
  onReset,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleChange = (key: keyof SearchFilters, value: any) => {
    onFilterChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== undefined && v !== '');

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Mobile toggle */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full lg:hidden flex items-center justify-between p-4 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500"
        aria-expanded={isExpanded}
      >
        <span className="font-medium text-gray-900">Filters</span>
        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {/* Filters content */}
      <div className={`p-4 space-y-6 ${isExpanded ? 'block' : 'hidden lg:block'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Filter Results</h3>
          {hasActiveFilters && (
            <button
              onClick={onReset}
              className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-green-500 rounded px-2 py-1"
            >
              <X className="w-4 h-4" />
              Reset
            </button>
          )}
        </div>

        {/* Author */}
        <div>
          <label htmlFor="filter-author" className="block text-sm font-medium text-gray-700 mb-2">
            Author
          </label>
          <input
            id="filter-author"
            type="text"
            value={filters.author || ''}
            onChange={(e) => handleChange('author', e.target.value)}
            placeholder="Author name"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Subject */}
        <div>
          <label htmlFor="filter-subject" className="block text-sm font-medium text-gray-700 mb-2">
            Subject
          </label>
          <input
            id="filter-subject"
            type="text"
            value={filters.subject || ''}
            onChange={(e) => handleChange('subject', e.target.value)}
            placeholder="Subject area"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Year Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Year Range
          </label>
          <div className="grid grid-cols-2 gap-2">
            <input
              id="filter-year-from"
              type="number"
              value={filters.year_from || ''}
              onChange={(e) => handleChange('year_from', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="From"
              min="1800"
              max="2100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              aria-label="Year from"
            />
            <input
              id="filter-year-to"
              type="number"
              value={filters.year_to || ''}
              onChange={(e) => handleChange('year_to', e.target.value ? Number(e.target.value) : undefined)}
              placeholder="To"
              min="1800"
              max="2100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
              aria-label="Year to"
            />
          </div>
        </div>

        {/* Availability */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Availability
          </label>
          <div className="space-y-2">
            {['available', 'loaned', 'reference'].map((status) => (
              <label key={status} className="flex items-center">
                <input
                  type="checkbox"
                  checked={filters.availability === status}
                  onChange={(e) => handleChange('availability', e.target.checked ? status : undefined)}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="ml-2 text-sm text-gray-700 capitalize">
                  {status === 'available' ? 'Available' : status === 'loaned' ? 'On Loan' : 'Reference Only'}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Language */}
        <div>
          <label htmlFor="filter-language" className="block text-sm font-medium text-gray-700 mb-2">
            Language
          </label>
          <select
            id="filter-language"
            value={filters.language || ''}
            onChange={(e) => handleChange('language', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
          >
            <option value="">All languages</option>
            <option value="en">English</option>
            <option value="hi">Hindi</option>
            <option value="as">Assamese</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default LibraryFilters;